import { useEffect, useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import toast, { Toaster } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import {ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, Copy, Loader2, LogOut, Plus, RefreshCw, Tag, XCircle} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { generateBatchCodes } from '@/lib/voucher'
import { useAuth } from '@/hooks/useAuth'

interface Voucher {
  _id: string
  code: string
  status: 'available' | 'used' | 'disabled'
  discountPercent?: number
  batchId?: string
  usedBy?: string
  createdAt: string
}

type Filter = 'all' | 'available' | 'used' | 'disabled'

const PAGE_SIZE = 50

function Admin() {
  const { isAuthenticated, isAdmin, user } = useAuth()
  const [authChecked, setAuthChecked] = useState(false)
  const [vouchers, setVouchers] = useState<Voucher[]>([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [quantity, setQuantity] = useState(10)
  const [discount, setDiscount] = useState(10)
  const [filter, setFilter] = useState<Filter>('all')
  const [page, setPage] = useState(1)
  const [totalFiltered, setTotalFiltered] = useState(0)
  const [stats, setStats] = useState({ total: 0, available: 0, used: 0, disabled: 0 })

  // Gate de autenticação: precisa estar logado e ser ADMIN
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isAuthenticated || !isAdmin) {
        window.location.href = '/admin/login'
        return
      }
      setAuthChecked(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [isAuthenticated, isAdmin])

  const fetchStats = async () => {
    try {
      // Busca contagens em paralelo (4 queries leves com limit:1)
      const [allRes, availRes, usedRes, disabledRes] = await Promise.all([
        lumi.entities.vouchers.list({ limit: 1 }),
        lumi.entities.vouchers.list({ filter: { status: 'available' }, limit: 1 }),
        lumi.entities.vouchers.list({ filter: { status: 'used' }, limit: 1 }),
        lumi.entities.vouchers.list({ filter: { status: 'disabled' }, limit: 1 }),
      ])
      setStats({
        total: (allRes as any)?.total ?? 0,
        available: (availRes as any)?.total ?? 0,
        used: (usedRes as any)?.total ?? 0,
        disabled: (disabledRes as any)?.total ?? 0,
      })
    } catch (err) {
      console.error('Fetch stats failed:', err)
    }
  }

  const fetchVouchers = async (targetPage: number, targetFilter: Filter) => {
    setLoading(true)
    try {
      const filterObj = targetFilter === 'all' ? {} : { status: targetFilter }
      const res: any = await lumi.entities.vouchers.list({
        filter: filterObj,
        sort: { createdAt: -1 },
        limit: PAGE_SIZE,
        skip: (targetPage - 1) * PAGE_SIZE,
      })
      setVouchers((res?.list as Voucher[]) ?? [])
      setTotalFiltered(res?.total ?? 0)
    } catch (err) {
      console.error('Fetch vouchers failed:', err)
      toast.error('Falha ao carregar vouchers.')
    } finally {
      setLoading(false)
    }
  }

  // Carrega stats + página inicial após auth
  useEffect(() => {
    if (authChecked) {
      fetchStats()
      fetchVouchers(1, filter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked])

  // Reage a mudança de filtro/página
  useEffect(() => {
    if (!authChecked) return
    fetchVouchers(page, filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter])

  const handleSignOut = async () => {
    try {
      await lumi.auth.signOut()
    } catch (err) {
      console.error('Sign out failed:', err)
    } finally {
      window.location.href = '/'
    }
  }

  const handleGenerate = async () => {
    if (generating) return
    if (quantity < 1 || quantity > 500) {
      toast.error('Quantidade entre 1 e 500.')
      return
    }
    if (discount < 0 || discount > 100) {
      toast.error('Desconto entre 0 e 100.')
      return
    }

    setGenerating(true)
    const loading = toast.loading(`Gerando ${quantity} vouchers...`)
    try {
      const codes = generateBatchCodes(quantity)
      const batchId = `batch_${Date.now()}`
      const now = new Date().toISOString()

      const docs = codes.map(code => ({
        code,
        status: 'available' as const,
        discountPercent: discount,
        batchId,
        creator: 'admin',
        createdAt: now,
        updatedAt: now,
      }))

      await lumi.entities.vouchers.createMany(docs)
      toast.dismiss(loading)
      toast.success(`${quantity} vouchers gerados!`)
      // Volta para a primeira página e recarrega stats
      setPage(1)
      await Promise.all([fetchStats(), fetchVouchers(1, filter)])
    } catch (err) {
      console.error('Generate failed:', err)
      toast.dismiss(loading)
      toast.error('Erro ao gerar vouchers.')
    } finally {
      setGenerating(false)
    }
  }

  const handleToggle = async (v: Voucher) => {
    if (v.status === 'used') {
      toast.error('Voucher já utilizado.')
      return
    }
    const next = v.status === 'available' ? 'disabled' : 'available'
    try {
      await lumi.entities.vouchers.update(v._id, {
        status: next,
        updatedAt: new Date().toISOString(),
      })
      toast.success(next === 'available' ? 'Reativado.' : 'Desativado.')
      await Promise.all([fetchStats(), fetchVouchers(page, filter)])
    } catch (err) {
      console.error('Toggle failed:', err)
      toast.error('Erro ao atualizar.')
    }
  }

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code).catch(() => {})
    toast.success('Código copiado!')
  }

  const handleExport = async () => {
    const loadingId = toast.loading('Preparando CSV...')
    try {
      // Para exportar, busca todos com o filtro atual em lotes
      const filterObj = filter === 'all' ? {} : { status: filter }
      const all: Voucher[] = []
      const batchSize = 500
      let skip = 0
      while (true) {
        const res: any = await lumi.entities.vouchers.list({
          filter: filterObj,
          sort: { createdAt: -1 },
          limit: batchSize,
          skip,
        })
        const batch = (res?.list as Voucher[]) ?? []
        all.push(...batch)
        if (batch.length < batchSize) break
        skip += batchSize
        if (skip > 50000) break // safety guard
      }
      const csv = ['code,status,discountPercent,batchId,createdAt']
        .concat(all.map(v => [v.code, v.status, v.discountPercent ?? 0, v.batchId ?? '', v.createdAt].join(',')))
        .join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `vouchers_${Date.now()}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.dismiss(loadingId)
      toast.success(`CSV exportado: ${all.length} vouchers.`)
    } catch (err) {
      console.error('Export failed:', err)
      toast.dismiss(loadingId)
      toast.error('Falha ao exportar.')
    }
  }

  const handleRefresh = async () => {
    await Promise.all([fetchStats(), fetchVouchers(page, filter)])
  }

  const handleFilterChange = (f: Filter) => {
    setFilter(f)
    setPage(1)
  }

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE)), [totalFiltered])
  const startIdx = totalFiltered === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalFiltered)

  // Enquanto valida autenticação, mostra spinner
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">Verificando acesso...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#ffffff', color: '#0F172A', border: '1px solid #E2E8F0', boxShadow: '0 10px 30px rgba(15,23,42,0.08)' },
        }}
      />

      <header className="border-b border-slate-200 sticky top-0 bg-white/80 backdrop-blur-md z-20">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Voltar</span>
          </Link>
          <h1 className="font-bold tracking-tight text-slate-900 text-sm sm:text-base">EW Pass · Minu — Painel de Vouchers</h1>
          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              className="p-2 text-slate-500 hover:text-slate-900 transition-colors"
              aria-label="Recarregar"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              aria-label="Sair"
              title={user?.email ? `Sair (${user.email})` : 'Sair'}
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Total" value={stats.total} accent="text-slate-900" />
          <StatCard label="Disponíveis" value={stats.available} accent="text-sky-600" />
          <StatCard label="Utilizados" value={stats.used} accent="text-teal-600" />
          <StatCard label="Desativados" value={stats.disabled} accent="text-slate-400" />
        </div>

        {/* Generator */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-2xl p-5 mb-8 shadow-sm"
        >
          <h2 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
            <Plus className="w-4 h-4 text-sky-500" />
            Gerar lote de vouchers
          </h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600 block mb-1.5">Quantidade</span>
              <input
                type="number"
                min={1}
                max={500}
                value={quantity}
                onChange={e => setQuantity(parseInt(e.target.value) || 0)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 block mb-1.5">Desconto (%)</span>
              <input
                type="number"
                min={0}
                max={100}
                value={discount}
                onChange={e => setDiscount(parseInt(e.target.value) || 0)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15"
              />
            </label>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 disabled:opacity-50 text-white font-bold rounded-lg px-4 py-2.5 flex items-center justify-center gap-2 transition mt-auto shadow-md shadow-sky-500/20"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Gerar
            </button>
          </div>
        </motion.div>

        {/* Filters & export */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'available', 'used', 'disabled'] as const).map(f => (
              <button
                key={f}
                onClick={() => handleFilterChange(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  filter === f
                    ? 'bg-gradient-to-r from-sky-500 to-teal-500 text-white shadow-sm'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {f === 'all' ? 'Todos' : f === 'available' ? 'Disponíveis' : f === 'used' ? 'Usados' : 'Desativados'}
              </button>
            ))}
          </div>
          <button
            onClick={handleExport}
            className="text-xs text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-lg px-3 py-1.5 transition"
          >
            Exportar CSV
          </button>
        </div>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {loading && vouchers.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Carregando...
            </div>
          ) : vouchers.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Tag className="w-6 h-6 mx-auto mb-2 opacity-50" />
              Nenhum voucher.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {vouchers.map(v => (
                <li key={v._id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition">
                  <StatusDot status={v.status} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="font-mono font-semibold text-sm tracking-wider text-slate-900">{v.code}</code>
                      {typeof v.discountPercent === 'number' && v.discountPercent > 0 ? (
                        <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">
                          -{v.discountPercent}%
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {v.status === 'used' && v.usedBy ? `Usado por ${v.usedBy}` : new Date(v.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCopy(v.code)}
                    className="p-2 text-slate-400 hover:text-slate-900 transition"
                    aria-label="Copiar"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  {v.status !== 'used' ? (
                    <button
                      onClick={() => handleToggle(v)}
                      className="p-2 text-slate-400 hover:text-slate-900 transition"
                      aria-label="Alternar status"
                    >
                      {v.status === 'available' ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {totalFiltered > 0 ? (
          <div className="flex items-center justify-between gap-3 mt-4 px-1">
            <p className="text-xs text-slate-500">
              {startIdx}–{endIdx} de <span className="font-semibold text-slate-700">{totalFiltered}</span>
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Página anterior"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Anterior</span>
              </button>
              <span className="text-xs text-slate-600 px-2 tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Próxima página"
              >
                <span className="hidden sm:inline">Próxima</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  )
}

function StatCard({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent}`}>{value}</p>
    </div>
  )
}

function StatusDot({ status }: { status: Voucher['status'] }) {
  const color =
    status === 'available' ? 'bg-sky-500' : status === 'used' ? 'bg-teal-500' : 'bg-slate-300'
  return <span className={`w-2 h-2 rounded-full ${color} flex-shrink-0`} />
}

export default Admin
